import { useLayoutEffect, useState } from "react";

import { QQ_PROGRESS_ATTR } from "@/components/forms/quick-quote-scroll";

/** Two-step progress indicator shared by both Quick Quote pages. */
export function QuickQuoteSteps({ current }: { current: 1 | 2 }) {
  const steps = [
    { n: 1 as const, label: "Your details" },
    { n: 2 as const, label: "Your address" },
  ];
  return (
    <ol className="mt-8 flex items-center gap-3" aria-label="Quick Quote progress">
      {steps.map((s, i) => {
        const active = s.n === current;
        const done = s.n < current;
        return (
          <li key={s.n} className="flex flex-1 items-center gap-3">
            <span
              aria-current={active ? "step" : undefined}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                active || done
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {s.n}
            </span>
            <span
              className={`text-sm font-semibold ${
                active ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {s.label}
            </span>
            {i === 0 && <span className="h-px flex-1 bg-border" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}
/** Number of steps in the quick quote: details, address, photos. */
export const QQ_STEP_COUNT = 3;

/**
 * Progress bar plus a visible "Step N of 3" line, used on every quick quote
 * step so the count reads the same on each page.
 *
 * Sticky (18 Oct 2026, Anthony): it stays pinned directly under the site
 * header while the visitor scrolls through the step, so the step is always
 * in view. Its top is the header's measured height (the header grows when
 * its phone menu opens). The current step's segment is gold; done steps are
 * dark. quick-quote-scroll.ts finds it by QQ_PROGRESS_ATTR and scrolls a
 * failed field to just under it.
 */
export function QuickQuoteStepLabel({ current, label }: { current: 1 | 2 | 3; label: string }) {
  const [top, setTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    const header = document.querySelector<HTMLElement>("header[data-site-header]");
    if (!header) return;
    const update = () =>
      setTop(getComputedStyle(header).position === "sticky" ? header.offsetHeight : 0);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      {...{ [QQ_PROGRESS_ATTR]: "" }}
      className="sticky top-16 z-30 -mx-4 border-b border-border bg-background px-4 py-2 lg:mx-0 lg:px-0"
      style={top === null ? undefined : { top }}
    >
      <p
        data-step-label
        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        Step {current} of {QQ_STEP_COUNT}
        <span className="font-normal normal-case tracking-normal">: {label}</span>
      </p>
      <div className="mt-2 flex gap-3" aria-hidden="true">
        {Array.from({ length: QQ_STEP_COUNT }, (_, i) => (
          <span
            key={i}
            data-step-segment={i + 1 === current ? "current" : i + 1 < current ? "done" : "next"}
            className={`h-1 flex-1 ${
              i + 1 === current ? "bg-primary" : i + 1 < current ? "bg-foreground" : "bg-border"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
